/**
 * @module GameSprite
 */

import GameObjectTools from "./game-object-tools.js";
import ResourceTypes from "./gf4/resource-types.js";

/**
 * Gamefroot physics sprite.
 * 
 * Even static images are implemented as sprites,
 * because users can interrogate animation data on any game object.
 * 
 * Boots up from `gamefroot-4` format data,
 * adds a ComponentManager, sets data `animKeyPrefix`,
 * and sets Gamefroot-default offsets etc.
 * 
 * @class GameSprite
 * @extends Phaser.Physics.Arcade.Sprite
 * @param {Phaser.Scene} scene - Level to which this sprite belongs
 * @param {object} data - Game object data from level JSON
 */
const GameSprite = new Phaser.Class( {
    Extends: Phaser.Physics.Arcade.Sprite,

    initialize: function GameSprite ( scene, data ) {

        // Assemble animations.
        const spriteResource =
            scene.game.resourceStore.getExternalResource( data, data.sprite ) ||
            {
                // Resource is broken. This will be picked up later.
                sequences: [],
            };
        const sequenceResources = spriteResource.sequences.map( sequence =>
            scene.game.resourceStore.getExternalResource(
                spriteResource, sequence) ) ||
            [
                // Resource is broken. This will be picked up later.
            ];
        /** Map of animation sequence names to unique IDs. */
        this.animIds = {};
        sequenceResources.forEach( sequence => {
            const anim = scene.getAnim( sequence );
            this.animIds[ sequence.name ] = anim.key;
        } );

        // Determine default texturing.
        let defaultTexture = 0;
        let defaultFrame = 0;
        const sequenceIdle =
            sequenceResources.find(res => res.name === "idle" ) ||
            sequenceResources[ 0 ];
        if ( !sequenceIdle ) {
            // Sprite has NO animations.
            console.error( "Game object has no animations:", data );
            const error = "Game object has no animations.\n" +
                "\n" +
                "Suggestion: locate and remove this object.\n" +
                "\n" +
                "Current level: " +
                    ( ( scene && scene.json && scene.json.name ) ||
                    "Unknown" ) + "\n" +
                "Objects beneath this: " + ( ( scene && scene.children && scene.children.list && scene.children.list.length ) || "0/Unknown" ) + "\n" +
                "Object type: Game object (graphics)\n" +
                "X position: " + data.position.x + "\n" +
                "Y position: " + data.position.y;
            Phaserfroot.game.reportError( error );
            return;
        } else {
            const anim = scene.anims.get( sequenceIdle.id );
            defaultTexture = anim.textureKey;
            defaultFrame = anim.textureFrame;
        }

        // Super
        Phaser.Physics.Arcade.Sprite.call(
            this,
            scene,
            data.position.x,
            data.position.y,
            defaultTexture,
            defaultFrame );

        /**
         * Supplemental physics data for tracking previous tile interactions
         * @type {Phaser.Types.Physics.Arcade.ArcadeBodyCollision}
         */
        this.wasBlocked = {
            none: true,
            left: false,
            right: false,
            up: false,
            down: false,
        },

        /**
         * Whether we're in the middle of starting up a new animation.
         * This is important for distinguishing between
         * `animationcomplete` events. When this is >0,
         * we are in the middle of a `playMy()` call,
         * and can assume we just started playing a new animation.
         * When it is `0`, we can assume we just ended a non-looping
         * animation.
         * @type {Number}
         */
        this.playStarting = 0;

        // Store gf4 resource for future reference.
        this.setData( "resource", data );

        // Store ID, coerced to type `String`.
        this.setData( "id", Phaserfroot.getUniqueId( data.id ) );

        // Store animation data.
        this.setData( "animKeyPrefix", this.getAnimPrefix( data ) );
        this.setData( "animKeyCurrent", "idle" );

        // Set up animation event forwarders.
        // Prior to Phaser 3.50, sprites would emit events with the name of an
        // animation in the event name. We rely on this for Blockly code.
        // So we'll just have to intercept those events and do them ourselves.
        const setAnimationEventForwarder = (eventName) => {
            this.on(eventName, (animation, ...rest) => {
                if (animation) {
                    this.emit(
                        eventName + "-" + animation.key, [animation, ...rest]);
                }
            });
        };
        setAnimationEventForwarder(Phaser.Animations.Events.ANIMATION_REPEAT);
        setAnimationEventForwarder(Phaser.Animations.Events.ANIMATION_START);
        setAnimationEventForwarder(Phaser.Animations.Events.ANIMATION_STOP);
        setAnimationEventForwarder(Phaser.Animations.Events.ANIMATION_UPDATE);

        // Play animation.
        this.play( `${sequenceIdle.id}` );

        // Set up Phaserfroot features.
        GameObjectTools.setCommonFeatures( this );
        data.tags.forEach((tag) => {
            this.tags.add(tag);
        });

        // Set physics size based on first frame.
        // Because `setSize` alters the offset, we must do it first.
        const firstFrameResource =
            scene.game.resourceStore.getExternalResource(
                sequenceIdle, sequenceIdle.frames[ 0 ] ) ||
            { hitboxes: [ {
                position: { x: 0, y: 0 },
                size: { x: 1, y: 1 },
            } ] };
        const box = firstFrameResource.hitboxes[ 0 ];
        this.body.setSize( box.size.x, box.size.y );
        this.body.setOffset( box.position.x, box.position.y );

        this.name = data.name;
        this.rotation = isNaN( data.rotation ) ? 0 : data.rotation;
        this.scaleX = isNaN( data.scale.x ) ? 1 : data.scale.x;
        this.scaleY = isNaN( data.scale.y ) ? 1 : data.scale.y;

        // Origin defaults to top left.
        this.anchorX = isNaN( data.anchor.x ) ? 0 : data.anchor.x;
        this.anchorY = isNaN( data.anchor.y ) ? 0 : data.anchor.y;

        // Integrate into scene.
        // `this.sys` refers to the same object as `this.physics.add.sys`,
        // which is used in `this.physics.add.sprite()`. Seems faster.
        scene.sys.displayList.add( this );
        scene.sys.updateList.add( this );

        // Initialize components after everything else is settled.
        this.setupPluginComponents( data );

        // Fire AFTER setting up components, so they may boot.
        this.emit( this.EVENTS.CREATE );
    },

    /**
     * Return a unique animation prefix for this game object.
     * @param {Object} data - Game object description
     * @return {string}
     */
    getAnimPrefix: function ( data ) {
        return (data.name ? data.name : data.id) + "-";
    },

    /**
     * Move on to the next frame, if the animation is playing.
     */
    playFrameNext: function () {
        this.playFrameRelative( 1 );
    },

    /**
     * Move back to the previous frame, if the animation is playing.
     */
    playFramePrevious: function () {
        this.playFrameRelative( -1 );
    },

    /**
     * Swap to a frame relative to this one. Wraps around the start/end.
     * Only works if the animation is playing.
     * @param {Number} step - number of frames to move ahead;
     *  negative moves backwards.
     */
    playFrameRelative: function ( step ) {
        step = Math.round( step );
        const anim = this.anims.currentAnim;
        if ( anim.paused ) {
            return;
        }
        const index = Phaser.Math.Wrap(
            this.playingIndex() + step, 0, anim.frames.length );
        this.anims.setCurrentFrame( anim.getFrameAt( index ) );
    },

    /**
     * Return the name of the current animation,
     * adapted from its unique internal code.
     * @returns {String|null}
     */
    playing: function () {

        // This should always be correct if animations are played
        // exclusively via `playMy()`:
        return this.getData( "animKeyCurrent" );

        // // Complex method for fetching anim key from ResourceStore:
        // if ( !this.anims ) {
        //     return null;
        // }
        // const anim = this.anims.currentAnim.key;
        // const resource =
        //     this.scene.game.resourceStore.getByIds([parseInt(anim)])[0];
        // if (!(resource && resource.type === ResourceTypes.Sequence)) {
        //     return null;
        // }
        // return resource.name;
    },

    /**
     * Return the index of the current animation frame.
     * NOTE: This is zero-based, and must be iterated once for use by Blockly.
     * @returns {Number}
     */
    playingIndex: function () {
        const frame = this.anims.currentFrame;
        const frames = this.anims.currentAnim.frames;
        if ( frame && frames ) {
            return frames.indexOf( frame );
        }
        return -1;
    },

    /**
     * Play an animation by its name.
     * Names are assumed to be unique.
     * If the animation is already playing, it will not restart.
     * @param {String} key - animation key root
     * @returns {GameSprite} This GameSprite
     * @chainable
     */
    playMy: function ( key ) {
        this.playStarting++;
        const playing = this.playing();
        const prefix = this.getData( "animKeyPrefix" );
        if ( prefix ) {
            const animKey = this.animIds[ key ];
            if (animKey === undefined) {
                // User has specified an animation that does not exist. Fail.
                console.warn("The animation", key, "does not exist! Animation was not changed.");
                return;
            }
            if ( playing !== key ) {
                // Animation switching counts as a stop.
                this.emit( this.EVENTS.ANIMATION_STOP );
                this.emit( this.EVENTS.ANIMATION_STOP +
                    "-" + prefix + playing );
                this.setData( "animKeyCurrent", key );
                this.play( animKey );
            }
        }
        this.playStarting--;
        return this;
    },

    /**
     * Pause animation playback. Use this instead of `anims.pause()`,
     * because this emits an `animationstop` event.
     */
    playStop: function () {
        this.anims.pause();
        this.emit( this.EVENTS.ANIMATION_STOP );
        this.emit( this.EVENTS.ANIMATION_STOP +
            "-" + this.anims.currentAnim.key );
    },

    /**
     * Create or remove an animation listener. Use this by preference over raw
     * listeners, because actual animation sequence key names are not obvious.
     */
    setAnimationListener: function (event, sequenceName, callback, context, remove) {
        const eventName = event + "-" + this.animIds[sequenceName];
        if (remove) {
            this.off(eventName, callback, context || this);
        } else {
            this.on(eventName, callback, context || this);
        }
    },
} );

export default GameSprite;
